import { MODULE_ROUTES } from '@constant/routes';
import type { AuthUser, PortalUserProfile } from '@type/auth';
import type { ModuleId } from '@type/module';

export const PORTAL_PASSWORD = 'Test@123';

export const ROLE_HOME_PATHS: Record<string, string> = {
  dashboard: MODULE_ROUTES.dashboard,
  coes: MODULE_ROUTES.coes,
  procurement: MODULE_ROUTES.procurement,
  contractmgmt: MODULE_ROUTES.contractmgmt,
  accounts: MODULE_ROUTES.accounts,
  training: MODULE_ROUTES.training,
  gap: MODULE_ROUTES.gap,
};

export const PORTAL_USERS: Record<string, PortalUserProfile> = {
  'pd.piwrpp@punjab.gov.pk': {
    role: 'dashboard',
    name: 'Miss Amna Rafiq',
    roleLabel: 'Project Director',
    modules: ['dashboard', 'visual'],
    defaultModule: 'dashboard',
  },
  'dd.civ.iwrpp@punjab.gov.pk': {
    role: 'coes',
    name: 'Zahid Hussain',
    roleLabel: 'Deputy Director / Project Manager (Civil Works)',
    modules: ['coes'],
    defaultModule: 'coes',
  },
  'dd.pnc.iwrpp@punjab.gov.pk': {
    role: 'procurement',
    name: 'Qasim Zia',
    roleLabel: 'Deputy Director Procurement & Contracts',
    modules: ['procurement', 'budget'],
    defaultModule: 'procurement',
  },
  'gen.sp.iwrpp@punjab.gov.pk': {
    role: 'gap',
    name: 'Naima Rashid',
    roleLabel: 'Gender Action Plan Officer',
    modules: ['gap'],
    defaultModule: 'gap',
  },
  'ad.met.iwrpp@punjab.gov.pk': {
    role: 'training',
    name: 'Muhammad Umer Saleem',
    roleLabel: 'Assistant Director M&E-Training',
    modules: ['training'],
    defaultModule: 'training',
    trainingComponents: [1, 2],
  },
  'mis.sp.iwrpp@punjab.gov.pk': {
    role: 'training_c3',
    name: 'Zaid Akbar',
    roleLabel: 'MIS Specialist',
    modules: ['training'],
    defaultModule: 'training',
    trainingComponents: [3],
  },
  'dd.eqpt.iwrpp@punjab.gov.pk': {
    role: 'proc_dd',
    name: 'Abbas Haider',
    roleLabel: 'Deputy Director (Equipment)',
    modules: ['procurement'],
    defaultModule: 'procurement',
    procScope: ['Goods'],
  },
  'ad.ets.iwrpp@punjab.gov.pk': {
    role: 'proc_ad',
    name: 'Usama Idrees',
    roleLabel: 'Assistant Director (ETS)',
    modules: ['procurement'],
    defaultModule: 'procurement',
    procScope: ['Goods'],
  },
  'dir.tne.iwrpp@punjab.gov.pk': {
    role: 'proc_dir',
    name: 'Khurram Shafiq Choudhary',
    roleLabel: 'Director Training & Equipment',
    modules: ['procurement', 'training', 'contractmgmt'],
    defaultModule: 'procurement',
    procScope: ['Goods'],
    trainingComponents: [1, 2],
    readOnly: true,
  },
  'ad.cm.iwrpp@punjab.gov.pk': {
    role: 'contractmgmt',
    name: 'Faisal Nadeem',
    roleLabel: 'Contract Management Officer',
    modules: ['contractmgmt'],
    defaultModule: 'contractmgmt',
  },
  'acc.off.iwrpp@punjab.gov.pk': {
    role: 'accounts',
    name: 'Shahid Iqbal',
    roleLabel: 'Accounts Officer',
    modules: ['accounts'],
    defaultModule: 'accounts',
  },
  'dd.fa.iwrpp@punjab.gov.pk': {
    role: 'accounts_dd',
    name: 'Avais Iqbal Khan',
    roleLabel: 'Deputy Director (Admin & Finance)',
    modules: ['accounts'],
    defaultModule: 'accounts',
  },
};

export const MODULE_LABELS: Record<ModuleId, string> = {
  dashboard: 'IWRPP · COE Programme Overview',
  coes: 'Civil Works Module',
  procurement: 'Procurement Module',
  contractmgmt: 'Contract Management',
  accounts: 'Accounts Management',
  training: 'Training Management',
  gap: 'Gender Action Plan',
  budget: 'Budget & Financials',
  visual: 'Visual Representation',
};

export function isProcInitiatorRole(user: Pick<AuthUser, 'role'> | null | undefined): boolean {
  return user?.role === 'proc_dd' || user?.role === 'proc_ad' || user?.role === 'proc_dir';
}

export function getInitiatorTabLabel(user: Pick<AuthUser, 'role' | 'procScope'> | null | undefined): string {
  const scope = user?.procScope;
  if (scope?.length === 1) return `Yet to Initiate ${scope[0]}`;
  if (scope?.length) return 'Yet to Initiate';
  if (user?.role === 'proc_dd') return 'Yet to Initiate Goods';
  if (user?.role === 'proc_ad') return 'Yet to Initiate Consultancy';
  if (user?.role === 'proc_dir') return 'Yet to Initiate Goods';
  return 'Yet to Initiate';
}

export function getInitiatorDefaultType(user: Pick<AuthUser, 'procScope'> | null | undefined): string {
  if (user?.procScope?.length === 1) return user.procScope[0];
  return user?.procScope?.[0] || 'Goods';
}

export function getAllowedTrainingComponents(user: Pick<AuthUser, 'trainingComponents'> | null | undefined): number[] {
  if (Array.isArray(user?.trainingComponents) && user.trainingComponents.length) {
    return user.trainingComponents;
  }
  return [1, 2, 3];
}

export function isTrainingC3OnlyRole(user: Pick<AuthUser, 'trainingComponents'> | null | undefined): boolean {
  const allowed = getAllowedTrainingComponents(user);
  return allowed.length === 1 && allowed[0] === 3;
}

export const SIDEBAR_MODULES: Array<{
  id: ModuleId;
  title: string;
  icon: string;
  tooltip: string;
}> = [
  { id: 'dashboard', title: 'Dashboard', icon: '/img/layout.png', tooltip: 'Dashboard' },
  { id: 'coes', title: 'Civil Works', icon: '/img/institution.png', tooltip: 'Civil Works' },
  { id: 'procurement', title: 'Equipment & Sourcing', icon: '/img/cart.png', tooltip: 'Equipment & Sourcing' },
  { id: 'contractmgmt', title: 'Contract Mgmt', icon: '/PIU-ICON/2.svg', tooltip: 'Contract Mgmt' },
  { id: 'accounts', title: 'Accounts', icon: '/PIU-ICON/7.svg', tooltip: 'Accounts' },
  { id: 'training', title: 'Training', icon: '/img/graduate.png', tooltip: 'Training' },
  { id: 'gap', title: 'Gender Action Plan', icon: '/PIU-ICON/8.svg', tooltip: 'GAP' },
  { id: 'budget', title: 'Budget', icon: '/PIU-ICON/7.svg', tooltip: 'Budget' },
  { id: 'visual', title: 'Visual Representation', icon: '/PIU-ICON/5.svg', tooltip: 'Visuals' },
];
